/**
 * Microsoft Clarity Custom Events
 * Shopify Theme: Sense
 *
 * Events:
 *  - add_to_cart
 *  - begin_checkout
 *  - search
 *  - lens_color_change
 *
 * IMPORTANT:
 * This file DOES NOT install Microsoft Clarity.
 * Microsoft Clarity should already be installed through the
 * official Shopify Microsoft Clarity app.
 *
 * Version: 1.0.0
 */

(function () {
  'use strict';

  /**
   * ------------------------------------------------------------
   * PREVENT SCRIPT FROM BEING INITIALIZED TWICE
   * ------------------------------------------------------------
   */

  if (window.__clarityCustomEventsInstalled) {
    return;
  }

  window.__clarityCustomEventsInstalled = true;

  /**
   * ------------------------------------------------------------
   * SETTINGS
   * ------------------------------------------------------------
   */

  const DEBUG = true;

  // Prevents the same event from firing twice because of
  // overlapping Shopify/theme events.
  const DEDUPE_TIME = 800;

  const lastEvents = new Map();

  /**
   * ------------------------------------------------------------
   * DEBUG LOGGER
   * ------------------------------------------------------------
   */

  function log() {
    if (!DEBUG) {
      return;
    }

    console.log(
      '[Clarity Custom Events]',
      ...arguments
    );
  }

  function warn() {
    if (!DEBUG) {
      return;
    }

    console.warn(
      '[Clarity Custom Events]',
      ...arguments
    );
  }

  /**
   * ------------------------------------------------------------
   * CLEAN VALUES BEFORE SENDING THEM TO CLARITY
   * ------------------------------------------------------------
   */

  function cleanValue(value) {
    if (value === undefined || value === null) {
      return '';
    }

    return String(value)
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 100);
  }

  /**
   * ------------------------------------------------------------
   * CHECK IF CLARITY EXISTS
   * ------------------------------------------------------------
   */

  function clarityIsAvailable() {
    return typeof window.clarity === 'function';
  }

  /**
   * ------------------------------------------------------------
   * SEND EVENT TO MICROSOFT CLARITY
   * ------------------------------------------------------------
   *
   * Example:
   *
   * sendClarityEvent('add_to_cart');
   *
   * Or:
   *
   * sendClarityEvent('lens_color_change', {
   *   tags: {
   *     lens_color: 'Blue'
   *   }
   * });
   *
   */

  function sendClarityEvent(eventName, options) {
    options = options || {};

    const dedupeKey =
      eventName +
      ':' +
      (options.dedupeKey || '');

    const now = Date.now();

    const previousTime =
      lastEvents.get(dedupeKey) || 0;

    /**
     * Prevent duplicate events.
     */
if (now - previousTime < DEDUPE_TIME) {
  return;
}

    lastEvents.set(
      dedupeKey,
      now
    );

    /**
     * Don't create or load Clarity ourselves.
     *
     * The official Microsoft Clarity Shopify integration
     * should control whether Clarity is available.
     */
    if (!clarityIsAvailable()) {
      warn(
        'Clarity is not available. Event not sent:',
        eventName
      );

      return;
    }

    try {
      /**
       * Add optional non-sensitive custom tags.
       */
      if (options.tags) {
        Object.keys(options.tags).forEach(function (tagName) {
          const value =
            cleanValue(options.tags[tagName]);

          if (!value) {
            return;
          }

          window.clarity(
            'set',
            tagName,
            value
          );
        });
      }

      /**
       * Send the actual Clarity custom event.
       */
      window.clarity(
        'event',
        eventName
      );

if (options.tags) {
  log(
    'Event sent:',
    eventName,
    options.tags
  );
} else {
  log(
    'Event sent:',
    eventName
  );
}
    } catch (error) {
      console.error(
        '[Clarity Custom Events] Error sending event:',
        eventName,
        error
      );
    }
  }

  /**
   * ------------------------------------------------------------
   * HELPER: GET FORM ACTION
   * ------------------------------------------------------------
   */

  function getFormPath(form) {
    try {
      const action =
        form.getAttribute('action') || '';

      return new URL(
        action,
        window.location.origin
      ).pathname;
    } catch (error) {
      return (
        form.getAttribute('action') ||
        ''
      );
    }
  }

  /**
   * ------------------------------------------------------------
   * HELPER: GET PRODUCT VARIANT ID
   * ------------------------------------------------------------
   *
   * We only use this internally for duplicate protection.
   * It is NOT sent to Clarity.
   */

  function getVariantId(form) {
    if (!form) {
      return '';
    }

    const variantInput =
      form.querySelector(
        '[name="id"]'
      );

    if (!variantInput) {
      return '';
    }

    return cleanValue(
      variantInput.value
    );
  }

  /**
   * ============================================================
   * 1. ADD TO CART
   * ============================================================
   *
   * Sense/Dawn-style Shopify product forms submit to:
   *
   * /cart/add
   * /cart/add.js
   *
   * Event delegation means this also works when Shopify
   * dynamically loads Quick Add or product sections.
   */

  document.addEventListener(
    'submit',
    function (event) {
      const form =
        event.target;

      if (
        !(form instanceof HTMLFormElement)
      ) {
        return;
      }

      const path =
        getFormPath(form);

      /**
       * ADD TO CART
       */
      if (
        path.includes('/cart/add')
      ) {
        const variantId =
          getVariantId(form);

        sendClarityEvent(
          'add_to_cart',
          {
            dedupeKey:
              variantId || 'product'
          }
        );

        return;
      }

      /**
       * SEARCH
       */
      if (
        path.includes('/search')
      ) {
        let searchQuery = '';

        try {
          const formData =
            new FormData(form);

          searchQuery =
            cleanValue(
              formData.get('q')
            );
        } catch (error) {
          // Ignore.
        }

        /**
         * Only count actual searches.
         *
         * IMPORTANT:
         * The customer's search text is NOT sent to Clarity.
         * This avoids accidentally sending personal information
         * entered into a search field.
         */
        if (searchQuery) {
          sendClarityEvent(
            'search',
            {
              dedupeKey: 'search'
            }
          );
        }

        return;
      }

      /**
       * BEGIN CHECKOUT THROUGH CART FORM
       *
       * This catches checkout buttons submitted through
       * Shopify's cart form.
       */
      const submitter =
        event.submitter;

      if (
        submitter instanceof Element &&
        (
          submitter.matches(
            '[name="checkout"]'
          ) ||
          submitter.closest(
            '[name="checkout"]'
          )
        )
      ) {
        sendClarityEvent(
          'begin_checkout',
          {
            dedupeKey: 'checkout'
          }
        );
      }
    },
    true
  );

  /**
   * ============================================================
   * 2. BEGIN CHECKOUT
   * ============================================================
   *
   * Covers:
   *
   * - Sense cart page Checkout button
   * - Cart drawer Checkout button
   * - Checkout links
   * - Dynamic checkout / Buy It Now buttons
   */

  const checkoutSelectors = [
    '[name="checkout"]',
    '.cart__checkout-button',
    'a[href*="/checkout"]',
    '.shopify-payment-button__button',
    '.shopify-payment-button__more-options'
  ];

  const checkoutSelector =
    checkoutSelectors.join(',');

  document.addEventListener(
    'click',
    function (event) {
      const target =
        event.target instanceof Element
          ? event.target
          : null;

      if (!target) {
        return;
      }

      const checkoutControl =
        target.closest(
          checkoutSelector
        );

      if (!checkoutControl) {
        return;
      }

      sendClarityEvent(
        'begin_checkout',
        {
          dedupeKey: 'checkout'
        }
      );
    },
    true
  );

  /**
   * ============================================================
   * 3. SEARCH
   * ============================================================
   *
   * The form submit listener above handles normal searches.
   *
   * This additional handler catches themes/components where
   * "Search for..." is represented directly by a /search link.
   */

  document.addEventListener(
    'click',
    function (event) {
      const target =
        event.target instanceof Element
          ? event.target
          : null;

      if (!target) {
        return;
      }

      const searchLink =
        target.closest(
          'a[href*="/search?"]'
        );

      if (!searchLink) {
        return;
      }

      let url;

      try {
        url = new URL(
          searchLink.href,
          window.location.origin
        );
      } catch (error) {
        return;
      }

      const query =
        cleanValue(
          url.searchParams.get('q')
        );

      if (!query) {
        return;
      }

      /**
       * Again, we deliberately DON'T send the search text
       * to Microsoft Clarity.
       */
      sendClarityEvent(
        'search',
        {
          dedupeKey: 'search'
        }
      );
    },
    true
  );

  /**
   * ============================================================
   * 4. LENS COLOR CHANGE
   * ============================================================
   *
   * Your Avatar Lens Preview is custom, so Shopify Sense
   * does not provide one universal lens-color selector.
   *
   * This code attempts to automatically detect common
   * implementations.
   *
   * It supports things such as:
   *
   * name="lens_color"
   * name="Lens Color"
   * name="properties[Lens Color]"
   * data-lens-color="Blue"
   * class="lens-color"
   * class="lens-color-input"
   * id="lens-color"
   * British spelling "colour" as well.
   */

  const directLensSelectors = [
    '[data-lens-color]',
    '[data-lens-colour]',

    'input[name*="lens" i][name*="color" i]',
    'input[name*="lens" i][name*="colour" i]',

    'select[name*="lens" i][name*="color" i]',
    'select[name*="lens" i][name*="colour" i]',

    'button[name*="lens" i][name*="color" i]',
    'button[name*="lens" i][name*="colour" i]',

    'input[id*="lens" i][id*="color" i]',
    'input[id*="lens" i][id*="colour" i]',

    'select[id*="lens" i][id*="color" i]',
    'select[id*="lens" i][id*="colour" i]',

    'button[id*="lens" i][id*="color" i]',
    'button[id*="lens" i][id*="colour" i]',

    '.lens-color-input',
    '.lens-colour-input',

    '[class*="lens-color" i]',
    '[class*="lens-colour" i]'
  ];

  const directLensSelector =
    directLensSelectors.join(',');

  /**
   * Containers that look like a lens selector area.
   */
  const lensContextSelector = [
    '[data-lens-color]',
    '[data-lens-colour]',
    '[class*="lens-color" i]',
    '[class*="lens-colour" i]',
    '[id*="lens-color" i]',
    '[id*="lens-colour" i]',
    '[class*="lens" i]',
    '[id*="lens" i]'
  ].join(',');

  /**
   * Determine whether an element appears to control
   * the Avatar Lens Color.
   */

  function isLensColorControl(element) {
    if (
      !element ||
      !(element instanceof Element)
    ) {
      return false;
    }

    /**
     * Strong/direct match.
     */
    try {
      if (
        element.matches(
          directLensSelector
        )
      ) {
        return true;
      }
    } catch (error) {
      // Ignore selector errors.
    }

    /**
     * Look at attributes on the control.
     */
    const attributes = [
      element.getAttribute('name'),
      element.getAttribute('id'),
      element.getAttribute('class'),
      element.getAttribute('aria-label'),
      element.getAttribute('data-color'),
      element.getAttribute('data-colour'),
      element.getAttribute('data-lens-color'),
      element.getAttribute('data-lens-colour')
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    const containsLens =
      attributes.includes('lens');

    const containsColor =
      attributes.includes('color') ||
      attributes.includes('colour');

    if (
      containsLens &&
      containsColor
    ) {
      return true;
    }

    /**
     * Handle structures such as:
     *
     * <div class="lens-options">
     *   <input name="Color">
     * </div>
     */

    const lensContainer =
      element.closest(
        lensContextSelector
      );

    if (!lensContainer) {
      return false;
    }

    const elementInfo = [
      element.getAttribute('name'),
      element.getAttribute('id'),
      element.getAttribute('class'),
      element.getAttribute('data-color'),
      element.getAttribute('data-colour')
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return (
      elementInfo.includes('color') ||
      elementInfo.includes('colour') ||
      element.hasAttribute('data-color') ||
      element.hasAttribute('data-colour')
    );
  }

  /**
   * ------------------------------------------------------------
   * GET SELECTED LENS COLOR
   * ------------------------------------------------------------
   */

  function getLensColor(element) {
    if (!element) {
      return '';
    }

    /**
     * data-lens-color
     */
    if (
      element.dataset &&
      element.dataset.lensColor
    ) {
      return cleanValue(
        element.dataset.lensColor
      );
    }

    /**
     * data-lens-colour
     */
    if (
      element.dataset &&
      element.dataset.lensColour
    ) {
      return cleanValue(
        element.dataset.lensColour
      );
    }

    /**
     * data-color
     */
    if (
      element.dataset &&
      element.dataset.color
    ) {
      return cleanValue(
        element.dataset.color
      );
    }

    /**
     * data-colour
     */
    if (
      element.dataset &&
      element.dataset.colour
    ) {
      return cleanValue(
        element.dataset.colour
      );
    }

    /**
     * SELECT dropdown
     */
    if (
      element instanceof HTMLSelectElement
    ) {
      const selectedOption =
        element.options[
          element.selectedIndex
        ];

      if (selectedOption) {
        return cleanValue(
          selectedOption.text ||
          selectedOption.value
        );
      }
    }

    /**
     * RADIO / INPUT
     */
    if (
      element instanceof HTMLInputElement
    ) {
      return cleanValue(
        element.value
      );
    }

    /**
     * VALUE attribute
     */
    if (
      element.getAttribute('value')
    ) {
      return cleanValue(
        element.getAttribute('value')
      );
    }

    /**
     * aria-label
     */
    if (
      element.getAttribute(
        'aria-label'
      )
    ) {
      return cleanValue(
        element.getAttribute(
          'aria-label'
        )
      );
    }

    /**
     * title
     */
    if (
      element.getAttribute('title')
    ) {
      return cleanValue(
        element.getAttribute(
          'title'
        )
      );
    }

    /**
     * Visible button text.
     */
    const text =
      cleanValue(
        element.textContent
      );

    if (
      text &&
      text.length <= 50
    ) {
      return text;
    }

    return '';
  }

  /**
   * ------------------------------------------------------------
   * SEND LENS COLOR EVENT
   * ------------------------------------------------------------
   */

  function trackLensColor(element) {
    const lensColor =
      getLensColor(element);

    const options = {
      dedupeKey:
        lensColor ||
        'lens-color'
    };

    /**
     * Lens color is not sensitive information,
     * so it is safe to use as a Clarity custom tag.
     */
    if (lensColor) {
      options.tags = {
        lens_color: lensColor
      };
    }

    sendClarityEvent(
      'lens_color_change',
      options
    );
  }

  /**
   * ------------------------------------------------------------
   * LENS COLOR: RADIO / SELECT / INPUT CHANGE
   * ------------------------------------------------------------
   */

  document.addEventListener(
    'change',
    function (event) {
      const target =
        event.target instanceof Element
          ? event.target
          : null;

      if (!target) {
        return;
      }

      /**
       * For radio buttons, only track the newly selected option.
       */
      if (
        target instanceof HTMLInputElement &&
        target.type === 'radio' &&
        !target.checked
      ) {
        return;
      }

      if (
        !isLensColorControl(target)
      ) {
        return;
      }

      trackLensColor(target);
    },
    true
  );

  /**
   * ------------------------------------------------------------
   * LENS COLOR: CUSTOM BUTTON / SWATCH CLICKS
   * ------------------------------------------------------------
   *
   * Some custom Avatar Preview systems don't use radio inputs.
   * They use:
   *
   * <button>
   * <div role="button">
   * <span data-lens-color>
   *
   * This handles those implementations.
   */

  document.addEventListener(
    'click',
    function (event) {
      const target =
        event.target instanceof Element
          ? event.target
          : null;

      if (!target) {
        return;
      }

      /**
       * Don't handle normal form inputs here.
       * The "change" listener above handles them.
       */
      if (
        target.closest(
          'input, select, option'
        )
      ) {
        return;
      }

      const control =
        target.closest(
          [
            'button',
            '[role="button"]',
            '[data-lens-color]',
            '[data-lens-colour]',
            '[data-color]',
            '[data-colour]',
            '.lens-color',
            '.lens-colour',
            '.lens-color-swatch',
            '.lens-colour-swatch'
          ].join(',')
        );

      if (!control) {
        return;
      }

      if (
        !isLensColorControl(control)
      ) {
        return;
      }

      trackLensColor(control);
    },
    true
  );

  /**
   * ============================================================
   * INITIALIZATION MESSAGE
   * ============================================================
   */

  log(
    'Loaded successfully.'
  );

  if (clarityIsAvailable()) {
    log(
      'Microsoft Clarity detected.'
    );
  } else {
    log(
      'Microsoft Clarity is not available yet. ' +
      'This can be normal if Clarity is loading asynchronously ' +
      'or analytics consent has not been granted.'
    );
  }
})();